import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { CopilotChatInputProps } from '@copilotkit/react-core/v2';
import { InkAiButton, InkAlert } from '@movable/ui';
import { Box, CircularProgress } from '@mui/material';

import { PopupViewProvider } from '@/components/shared/AgentAssistantPopup/PopupViewContext';
import type { PopupView } from '@/components/shared/AgentAssistantPopup/PopupViewContext';
import { ConversationHistoryList } from '@/components/shared/AgentAssistantPopup/slots/ConversationHistoryList';
import { InkAssistantWelcome } from '@/components/shared/AgentAssistantPopup/slots/InkAssistantWelcome';
import { InkChatInputSlot } from '@/components/shared/AgentAssistantPopup/slots/InkChatInputSlot';
import {
  ChatBodyRoot,
  Disclaimer,
  Footer,
  Launcher,
  Surface,
} from '@/components/shared/AgentAssistantPopup/styles';
import type { AgentAssistantPopupProps } from '@/components/shared/AgentAssistantPopup/types';
import { useEnterExitTransition } from '@/components/shared/AgentAssistantPopup/useEnterExitTransition';
import { copyRenderedSelection } from '@/components/shared/AgentAssistantPopup/utils';
import { useAgentChat } from '@/hooks/useAgentChat';
import { useSnackbar } from '@/providers/SnackbarProvider/SnackbarProvider';

import { MessageList } from './MessageList';
import { PopupHeader } from './PopupHeader';

const DISCLAIMER_TEXT =
  'This feature uses Generative AI. AI-generated content may be inaccurate or incomplete. Please review before use.';

const RUN_ERROR_TEXT =
  'This conversation encountered an error. Start a new chat to keep going.';

/** How long the popup surface stays mounted after close so its exit transition can finish. */
const SURFACE_EXIT_MS = 300;
/** How long the welcome state stays mounted after it's dismissed so its exit transition can finish. */
const WELCOME_EXIT_MS = 200;

/**
 * The agent-assistant popup. Renders its own popup shell + launcher and drives
 * the conversation through the headless CopilotKit hooks (`useAgent` /
 * `useCopilotKit`); the CopilotKit runtime + provider are supplied by the caller.
 *
 * Composed of:
 *  - the launcher FAB + open/close state + sizing,
 *  - the message list (`MessageList`) and its autoscroll / stick-to-bottom,
 *  - the empty/welcome state (rendered directly when the thread is empty),
 *  - the input + disclaimer footer.
 *
 * Shared building blocks: `PopupViewContext`, `InkChatInputSlot`,
 * `InkAssistantMessageSlot`, `InkUserMessageSlot`, `InkAssistantWelcome`,
 * `ConversationHistoryList`, `groupSessionsByDate`.
 */
const AgentAssistantPopup = ({
  agentId,
  threadId,
  labels,
  placeholder,
  defaultOpen = false,
  clickOutsideToClose,
  welcome,
  history,
  feedback,
  showBetaTag = false,
}: AgentAssistantPopupProps) => {
  const [open, setOpen] = useState(defaultOpen);
  const [view, setViewState] = useState<PopupView>('chat');
  const [expanded, setExpanded] = useState(false);
  // `InkAiChatInput` is a controlled component (`value` + `onChange` required),
  // so the composer text lives here and is threaded through the input slot.
  const [inputValue, setInputValue] = useState('');

  const snackbar = useSnackbar();

  // Only restore from the backend when reopening an existing conversation. A
  // thread is "existing" iff it's in the history list — selecting a past thread
  // requires history to be loaded first, while freshly-minted thread ids never
  // appear there. This keeps a newly-opened popup from firing an empty backend
  // run before the user has sent a message.
  const shouldRestore = useMemo(
    () =>
      Boolean(
        threadId &&
          history?.sessions.some((session) => session.sessionId === threadId),
      ),
    [threadId, history?.sessions],
  );

  const {
    messages,
    isRunning,
    chatName,
    isRestoring,
    runError: liveRunError,
    sendMessage,
    stop,
  } = useAgentChat(agentId, threadId, shouldRestore);

  const viewThreadIdRef = useRef(threadId);
  if (threadId !== viewThreadIdRef.current) {
    viewThreadIdRef.current = threadId;
    setViewState('chat');
  }

  const setView = useCallback(
    (next: PopupView) => {
      if (next === 'history') history?.onOpen?.();
      setViewState(next);
    },
    [history],
  );

  const handleCopyMessage = useCallback(
    async (content: string, messageIds: string[]) => {
      try {
        if (await copyRenderedSelection(messageIds)) {
          snackbar({ severity: 'success', message: 'Copied to clipboard!' });
          return;
        }

        await navigator.clipboard.writeText(content);
        snackbar({ severity: 'success', message: 'Copied to clipboard!' });
      } catch {
        snackbar({ severity: 'error', message: 'Could not copy' });
      }
    },
    [snackbar],
  );

  const submitVote = useCallback(
    async (vote: 'up' | 'down', comment?: string) => {
      if (!feedback) return;

      try {
        await feedback.onSubmit({ vote, comment });
        snackbar({
          severity: 'success',
          message: 'Thank you for your feedback!',
        });
      } catch {
        snackbar({
          severity: 'error',
          message: 'Could not submit your feedback',
        });
      }
    },
    [feedback, snackbar],
  );

  const popupViewValue = useMemo(
    () => ({ view, setView, expanded, setExpanded, history }),
    [view, setView, expanded, history],
  );

  // A reopened thread won't re-emit `chat_name` (that fires once, on the first
  // run of a new thread), so fall back to the title persisted in the history
  // list for the current thread. `null` (an unnamed thread) falls through.
  const persistedSession = history?.sessions.find(
    (session) => session.sessionId === threadId,
  );
  const persistedName = persistedSession?.chatName;

  // A thread is unrecoverable once it has hit a RUN_ERROR — either live, in
  // the current session, or as recorded on a past conversation reopened from
  // history (`run_error` in `GET /api/v1/chats`).
  const runError = liveRunError ?? persistedSession?.runError ?? null;

  // Prefer the live event name (freshest, current thread), then the persisted
  // name for a reopened thread, then the configured label / generic default.
  const title =
    chatName ?? persistedName ?? labels?.modalHeaderTitle ?? 'Ask AI';
  // The beta tag only belongs on the untitled/default state, not once a thread
  // has been named — so key off the absence of a name rather than the title text.
  const isUnnamedThread = !chatName && !persistedName;

  const handleClose = useCallback(() => setOpen(false), []);

  const { mounted: surfaceMounted, active: surfaceVisible } =
    useEnterExitTransition(open, SURFACE_EXIT_MS, { deferEnter: true });

  // Focus management. The composer's `autoFocus` moves focus into the dialog on
  // open; on close we return focus to the launcher so keyboard users aren't
  // dropped at the top of the page.
  const launcherRef = useRef<HTMLButtonElement>(null);
  const wasOpenRef = useRef(open);
  useEffect(() => {
    if (wasOpenRef.current && !open) launcherRef.current?.focus();
    wasOpenRef.current = open;
  }, [open]);

  // Submit the typed text, then clear the composer. `sendMessage` already trims
  // and ignores empty/whitespace-only input, so blank submits are no-ops.
  const handleSubmitMessage = useCallback(
    (text: string) => {
      sendMessage(text);
      setInputValue('');
    },
    [sendMessage],
  );

  // The input slot is typed against CopilotKit's `CopilotChatInputProps`; we
  // reuse it unchanged by building that exact prop shape from our headless state.
  // `InkAiChatInput` is controlled, so `value`/`onChange` must be wired through.
  const inputProps: CopilotChatInputProps = {
    value: inputValue,
    onChange: setInputValue,
    onSubmitMessage: handleSubmitMessage,
    onStop: stop,
    isRunning,
    autoFocus: true,
  };

  // Suppress the welcome screen while restoring: messages are cleared
  // synchronously on thread switch but the snapshot lands a beat later, so
  // gating only on `messages.length === 0` would flash the empty state.
  const showWelcome = Boolean(welcome) && messages.length === 0 && !isRestoring;

  const { mounted: welcomeMounted, active: welcomeActive } =
    useEnterExitTransition(showWelcome, WELCOME_EXIT_MS);
  const welcomeExiting = !welcomeActive;

  const renderBody = () => {
    if (view === 'history') return <ConversationHistoryList />;
    if (isRestoring && messages.length === 0) {
      return (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'background.paper',
          }}
        >
          <CircularProgress size={24} />
        </Box>
      );
    }
    return (
      <ChatBodyRoot>
        {!showWelcome && (
          <MessageList
            messages={messages}
            isRunning={isRunning}
            onThumbsUp={
              feedback
                ? () => {
                    void submitVote('up');
                  }
                : undefined
            }
            onThumbsDown={
              feedback
                ? (_message, { issueType, comment }) => {
                    const trimmedComment = comment.trim();

                    const builtComment = trimmedComment
                      ? `Type: ${issueType}\n${trimmedComment}`
                      : `Type: ${issueType}`;

                    void submitVote('down', builtComment);
                  }
                : undefined
            }
            onCopy={handleCopyMessage}
          />
        )}
        {welcomeMounted && welcome && (
          <InkAssistantWelcome
            welcome={welcome}
            onSelectSuggestion={sendMessage}
            exiting={welcomeExiting}
          />
        )}
      </ChatBodyRoot>
    );
  };

  return (
    <PopupViewProvider value={popupViewValue}>
      {open && clickOutsideToClose && (
        <Box
          onClick={handleClose}
          sx={{ position: 'fixed', inset: 0, zIndex: 1299 }}
        />
      )}

      {surfaceMounted && (
        <Surface
          isOpen={surfaceVisible}
          isExpanded={expanded}
          elevation={0}
          role="dialog"
          aria-modal={clickOutsideToClose ? true : undefined}
          aria-label={title}
          onKeyDown={(event) => {
            if (event.key === 'Escape') handleClose();
          }}
        >
          <PopupHeader
            showBetaTag={showBetaTag && isUnnamedThread}
            title={title}
            onClose={handleClose}
            hideNewChat={view === 'chat' && showWelcome}
          />
          {renderBody()}
          {/* The composer is hidden in history view, matching the old popup. */}
          {view === 'chat' && (
            <Footer>
              {runError ? (
                <>
                  <InkAlert
                    message={RUN_ERROR_TEXT}
                    alertProps={{
                      severity: 'error',
                      sx: { margin: '0 16px 16px' },
                    }}
                  />
                  <InkChatInputSlot
                    {...inputProps}
                    placeholder={placeholder}
                    disabled
                  />
                </>
              ) : (
                <InkChatInputSlot {...inputProps} placeholder={placeholder} />
              )}
              <Disclaimer variant="caption">{DISCLAIMER_TEXT}</Disclaimer>
            </Footer>
          )}
        </Surface>
      )}

      <Launcher
        ref={launcherRef}
        isHidden={open}
        aria-label="Open assistant"
        aria-hidden={open}
        tabIndex={open ? -1 : 0}
        onClick={() => setOpen(true)}
      >
        <InkAiButton icon="ink-ai" size="large" tabIndex={-1}>
          Ask AI
        </InkAiButton>
      </Launcher>
    </PopupViewProvider>
  );
};

export default AgentAssistantPopup;
